import { set } from '../../utils/ramdaUtils';
import {
  ADD_DISTRIBUTION_LIST_ITEM,
  ADD_DISTRIBUTION_LIST_ITEMS,
  ADD_SAVED_SEARCH,
  ADD_TABLE_ROW,
  ADD_VALIDATION_ERRORS,
  CLEAR_SAVED_SEARCH_DESIGNER,
  COPY_SAVED_SEARCH,
  DELETE_DISTRIBUTION_LIST_ITEM,
  DELETE_SAVED_SEARCH_NODE,
  DELETE_TABLE_ROW,
  DELETE_VALIDATION_ERRORS,
  EDIT_SAVED_SEARCH,
  EDIT_TABLE_ROW,
  LOAD_ENTITY_FIELDS,
  LOAD_SAVED_SEARCHES,
  MOVE_TABLE_ROW,
  SET_SELECTED_SAVED_SEARCH_ID,
  SET_SELECTED_TABLE_ROW,
} from '../actions/editor.actions';
import { COLUMNS_IS_NAVIGATION_COLUMN, MOVE_TABLE_ROW_UP } from '../constants/editor.constants';
import { REPOSITORY_SAVED_SEARCH_TEMPLATE } from '../constants/new-saved-search-template';
import { modifySavedSearches } from '../utils/common.utils';

const savedSearchDesignerReducer = (state = {}, action) => {
  switch (action.type) {
    case LOAD_SAVED_SEARCHES: {
      const { savedSearches, selectedSavedSearchId, isDocRepository = false } = action.payload;

      const modifiedSavedSearches = modifySavedSearches(savedSearches);

      return {
        ...state,
        selectedSavedSearchId,
        isDocRepository,
        savedSearches: modifiedSavedSearches,
      };
    }

    case LOAD_ENTITY_FIELDS: {
      return {
        ...state,
        entityFields: action.payload.entityFields,
        entityTreeFields: action.payload.entityTreeFields,
      };
    }

    case CLEAR_SAVED_SEARCH_DESIGNER: {
      return {};
    }

    case ADD_SAVED_SEARCH: {
      const { newSavedSearchId } = action.payload;

      return {
        ...state,
        selectedSavedSearchId: newSavedSearchId,
        savedSearches: {
          ...state.savedSearches,
          [newSavedSearchId]: {
            savedSearch: REPOSITORY_SAVED_SEARCH_TEMPLATE,
            originalSavedSearch: REPOSITORY_SAVED_SEARCH_TEMPLATE,
          },
        },
      };
    }

    case COPY_SAVED_SEARCH: {
      const { newSavedSearchId, originalSavedSearchId } = action.payload;

      const copiedSavedSearch = state.savedSearches[originalSavedSearchId];

      return {
        ...state,
        selectedSavedSearchId: newSavedSearchId,
        savedSearches: {
          ...state.savedSearches,
          [newSavedSearchId]: copiedSavedSearch,
        },
      };
    }

    case DELETE_SAVED_SEARCH_NODE: {
      const { savedSearchId } = action.payload;

      const savedSearches = { ...state.savedSearches };
      delete savedSearches[savedSearchId];

      return { ...state, savedSearches };
    }

    case EDIT_SAVED_SEARCH: {
      const { selectedSavedSearchId } = state;
      const propPath = action.payload.prop?.split('.') || [];

      return set(
        state,
        ['savedSearches', selectedSavedSearchId, 'savedSearch', ...propPath],
        action.payload.value
      );
    }

    case ADD_TABLE_ROW: {
      const { selectedSavedSearchId } = state;
      const { tableName, newRow } = action.payload;

      const tableRows = state.savedSearches[selectedSavedSearchId].savedSearch[tableName];

      return set(
        state,
        ['savedSearches', selectedSavedSearchId, 'savedSearch', tableName],
        [...tableRows, newRow]
      );
    }

    case EDIT_TABLE_ROW: {
      const { selectedSavedSearchId } = state;
      const { tableName, key, rowId, value } = action.payload;

      const updatedRows = state.savedSearches[selectedSavedSearchId].savedSearch[tableName].map(
        (row) => {
          const isNavigationColumn = key === COLUMNS_IS_NAVIGATION_COLUMN;
          if (row.rowId !== rowId) {
            return isNavigationColumn ? { ...row, isNavigationColumn: false } : row;
          }

          return { ...row, [key]: value };
        }
      );

      return set(
        state,
        ['savedSearches', selectedSavedSearchId, 'savedSearch', tableName],
        updatedRows
      );
    }

    case DELETE_TABLE_ROW: {
      const { selectedSavedSearchId } = state;
      const { tableName, rowId } = action.payload;

      const updatedRows = state.savedSearches[selectedSavedSearchId].savedSearch[tableName].filter(
        (row) => row.rowId !== rowId
      );

      return set(
        state,
        ['savedSearches', selectedSavedSearchId, 'savedSearch', tableName],
        updatedRows
      );
    }

    case MOVE_TABLE_ROW: {
      const { selectedSavedSearchId } = state;
      const { tableName, currentIndex, moveAction } = action.payload;

      const newRows = [...state.savedSearches[selectedSavedSearchId].savedSearch[tableName]];
      const currentItem = newRows[currentIndex];
      const swapItemIndex = moveAction === MOVE_TABLE_ROW_UP ? currentIndex - 1 : currentIndex + 1;
      newRows[currentIndex] = newRows[swapItemIndex];
      newRows[swapItemIndex] = currentItem;

      return set(
        state,
        ['savedSearches', selectedSavedSearchId, 'savedSearch', tableName],
        newRows
      );
    }

    case SET_SELECTED_SAVED_SEARCH_ID: {
      return {
        ...state,
        selectedSavedSearchId: action.payload.searchId,
      };
    }

    case SET_SELECTED_TABLE_ROW: {
      return {
        ...state,
        selectedTableRow: {
          rowId: action.payload.rowId,
        },
      };
    }

    case ADD_DISTRIBUTION_LIST_ITEM: {
      const { selectedSavedSearchId } = state;

      const currentDistributionList =
        state.savedSearches[selectedSavedSearchId].savedSearch.distribution.distributionList;

      return set(
        state,
        ['savedSearches', selectedSavedSearchId, 'savedSearch', 'distribution', 'distributionList'],
        [action.payload.newRow, ...currentDistributionList]
      );
    }

    case ADD_DISTRIBUTION_LIST_ITEMS: {
      const { selectedSavedSearchId } = state;

      const currentDistributionList =
        state.savedSearches[selectedSavedSearchId].savedSearch.distribution.distributionList;

      return set(
        state,
        ['savedSearches', selectedSavedSearchId, 'savedSearch', 'distribution', 'distributionList'],
        [...action.payload.rows, ...currentDistributionList]
      );
    }

    case DELETE_DISTRIBUTION_LIST_ITEM: {
      const { selectedSavedSearchId } = state;

      const updatedRows = state.savedSearches[
        selectedSavedSearchId
      ].savedSearch.distribution.distributionList.filter(
        (row) => row.rowId !== action.payload.rowId
      );
      return set(
        state,
        ['savedSearches', selectedSavedSearchId, 'savedSearch', 'distribution', 'distributionList'],
        updatedRows
      );
    }

    case ADD_VALIDATION_ERRORS: {
      return {
        ...state,
        validationErrors: action.payload,
      };
    }

    case DELETE_VALIDATION_ERRORS: {
      return {
        ...state,
        validationErrors: [],
      };
    }

    default:
      return state;
  }
};

export default savedSearchDesignerReducer;
